import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { ContentAttachments } from '@/components/widget/content-attachments';
import { extractMessageObligations } from '@/api/content-api';
import { invalidateObligationViews } from '@/hooks/useObligationQueries';
import { useQueryClient } from '@tanstack/react-query';
import {
  useMarkAsRead,
  useNavigateWithContentFlush,
  useCreateMessageReplyDraft,
  useSendMessageDraft,
  useUser,
} from '@/hooks';
import { executeTool } from '@/api/tools-api';
import { CANONICAL_LABELS } from '@/lib/content-labels';
import { getAxiosErrorDisplayMessage } from '@/utils/error-utils';
import { toContentHref } from '@/utils/content-ref';
import {
  type Message,
  type MessageDraftMode,
} from '@vivport/backend/types';
import {
  ChevronsDownUp,
  ChevronsUpDown,
  Ellipsis,
  Forward,
  Info,
  Loader2,
  Reply,
  SmilePlus,
} from 'lucide-react';
import type { ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Markdown from 'react-markdown';
import { toast } from 'sonner';
import { useComposerStore } from '@/store/useComposerStore';
import { useViewContextStore } from '@/store/viewContextStore';
import { createContent } from '@/api/content-api';
import { addContentAttachment } from '@/api/content-attachment-api';
import {
  draftFieldsFromLists,
  splitRecipients,
  useMessageDraftLifecycle,
  type DraftFields,
} from '@/features/messaging/useMessageDraftLifecycle';
import {
  buildComposerPrefillFromDefaults,
  buildComposerPrefillFromResumable,
  EMPTY_COMPOSER_DRAFT,
  MessageInlineReplyComposer,
  type ComposerDraftState,
} from './DetailsMessageComposer';
import {
  highlightQuoteInMessageBody,
  EXTRACT_QUOTE_HIGHLIGHT_NAME,
  MessageConversationItem,
  getExpandedSanitizedHtml,
  getExpandedBodyParts,
  shouldCenterRichHtmlBlock,
  splitHtmlAtFirstRichLayoutBoundary,
  parseMessage,
  joinConversationAddresses,
  formatReadableTimestamp,
  getConversationTimelineAt,
  getReadableMessageBody,
  getDisplayName,
  getAvatarText,
  getMemberAvatarText,
  getRecipientSummary,
  getCollapsedPreview,
  getSenderMailDomain,
  resolveConversationSourceItem,
} from './message-body';
import {
  MESSAGE_HIGHLIGHT_QUOTE_EVENT,
  isMessageHighlightQuoteDetail,
  type MessageHighlightQuoteDetail,
} from '@/components/widget/message-events';
import { useLocation } from 'react-router-dom';
import { Notice } from '@/components/ui/notice';

const MESSAGE_TOGGLE_DRAFT_EVENT = 'vivport:message-toggle-draft-composer';
const MESSAGE_TOGGLE_PROMOTE_EVENT = 'vivport:message-toggle-promote-panel';
const MESSAGE_RUN_EXTRACTION_EVENT = 'vivport:message-run-extraction';

/**
 * Highlights every occurrence of `quote` inside the rendered message thread using
 * the CSS Custom Highlight API. The quote text is whitespace-normalized (it came
 * from the extraction prompt's normalized body), so matching walks the DOM text
 * nodes building a whitespace-collapsed string with a char→(node, offset) map.
 */
interface DetailsMessageProps {
  content: Message;
  memberId: number;
}

const QUICK_REPLY_CHIPS: readonly string[] = ['Thanks!', 'Got it.', 'Will do.'];

const messageBodyMarkdownComponents = {
  p: ({ children }: { children?: ReactNode }) => <p className="mb-2 last:mb-0">{children}</p>,
  a: ({ href, children }: { href?: string; children?: ReactNode }) => (
    <a
      href={href}
      className="font-medium text-primary underline underline-offset-2"
      target="_blank"
      rel="noreferrer noopener"
    >
      {children}
    </a>
  ),
};

const messageBodyProseClassName =
  'prose prose-sm dark:prose-invert max-w-none break-words px-[52px] text-[15px] leading-7 text-foreground [&>*:first-child]:mt-0 [&>*:last-child]:mb-0 [&_a]:text-primary [&_img]:max-h-96 [&_img]:max-w-full [&_table]:max-w-full';

/**
 * HTML from Gmail and other providers is usually table-based with inline widths.
 * Tailwind Typography `prose` adds borders, padding, and image rules that break those layouts.
 */
const messageBodyHtmlEmailBaseClassName =
  'not-prose max-w-none break-normal text-[15px] leading-7 text-foreground ' +
  '[&>*:first-child]:mt-0 [&>*:last-child]:mb-0 ' +
  '[&_a]:font-medium [&_a]:text-primary [&_a]:underline [&_a]:underline-offset-2 ' +
  '[&_table]:border-collapse [&_table]:border-0 [&_table]:m-0 [&_table]:bg-transparent ' +
  '[&_td]:!border-0 [&_td]:!p-0 [&_td]:!m-0 [&_td]:align-top [&_td]:align-middle ' +
  '[&_th]:!border-0 [&_th]:!p-0 [&_th]:align-top ' +
  '[&_img]:inline-block [&_img]:max-w-full [&_img]:h-auto [&_img]:border-0 [&_img]:!m-0 [&_img]:align-middle ' +
  '[&_tbody]:border-0 [&_thead]:border-0 [&_tfoot]:border-0';

const messageBodyHtmlEmailClassName = `${messageBodyHtmlEmailBaseClassName} px-[52px]`;
const messageBodyHtmlEmailCenteredClassName = `${messageBodyHtmlEmailBaseClassName} mx-auto w-fit max-w-full px-[52px]`;

/** Same horizontal inset as the ellipsis row so the quote bar lines up with the button’s left edge. */
const messageBodyQuotedShellClassName = 'mt-2 px-[52px]';

const messageBodyQuotedInnerClassName =
  'prose prose-sm dark:prose-invert max-w-none break-words border-l-4 border-border bg-muted/25 py-2 pl-3 pr-2 text-[15px] leading-7 text-foreground rounded-r-md [&>*:first-child]:mt-0 [&>*:last-child]:mb-0 [&_a]:text-primary [&_img]:max-h-96 [&_img]:max-w-full [&_table]:max-w-full';

const messageBodyHtmlEmailQuotedInnerClassName =
  `${messageBodyHtmlEmailBaseClassName} border-l-4 border-border bg-muted/25 py-2 pl-3 pr-2 rounded-r-md`;

export function DetailsMessage({ content }: DetailsMessageProps) {
  const navigate = useNavigateWithContentFlush();
  const userQuery = useUser();
  const createReplyDraftMutation = useCreateMessageReplyDraft();
  const sendDraftMutation = useSendMessageDraft();
  const markAsReadMutation = useMarkAsRead();
  const markedReadForContentIdRef = useRef<number | null>(null);
  const [composerOpen, setComposerOpen] = useState(false);
  const [composerMode, setComposerMode] = useState<MessageDraftMode>('reply');
  const [composerDraft, setComposerDraft] = useState<ComposerDraftState>({ ...EMPTY_COMPOSER_DRAFT });
  const [composerAttachments, setComposerAttachments] = useState<File[]>([]);
  const [composerDirty, setComposerDirty] = useState(false);
  const [isSendingInline, setIsSendingInline] = useState(false);
  const [boundDraftContentId, setBoundDraftContentId] = useState<number | null>(null);
  const overlayComposerOpen = useComposerStore((s) => s.isOpen);
  useEffect(() => {
    if (!composerOpen || overlayComposerOpen) return;
    useViewContextStore.getState().setSlot('messageComposer', {
      kind: 'reply',
      mode: composerMode,
      sourceContentId: content.id,
      ...(boundDraftContentId !== null ? { draftContentId: boundDraftContentId } : {}),
      unsaved: {
        to: composerDraft.to,
        cc: composerDraft.cc,
        bcc: composerDraft.bcc,
        subject: composerDraft.subject,
        body: composerDraft.authoredBody,
      },
    });
    return () => useViewContextStore.getState().clearSlot('messageComposer');
  }, [
    composerOpen,
    overlayComposerOpen,
    composerMode,
    content.id,
    boundDraftContentId,
    composerDraft.to,
    composerDraft.cc,
    composerDraft.bcc,
    composerDraft.subject,
    composerDraft.authoredBody,
  ]);
  const [showPromoteEditor, setShowPromoteEditor] = useState(false);
  const [isPromoting, setIsPromoting] = useState(false);
  const queryClient = useQueryClient();
  const isExtractingRef = useRef(false);
  const sourceConversationItem = resolveConversationSourceItem(content);
  const conversationItems = useMemo(
    () => content.conversation?.items ?? [],
    [content.conversation]
  );
  const { parsed, errors } = parseMessage(content);

  const messageDraftDefaults = content.messageDraftDefaults ?? null;
  const replyCapable = messageDraftDefaults?.replyCapable === true;
  const composeAccountId = messageDraftDefaults?.actingAccountId ?? null;
  const composerMemberEmail =
    messageDraftDefaults?.actingEmail ?? userQuery.data?.selectedMember.email ?? null;
  const composerMemberAvatarText = getMemberAvatarText(composerMemberEmail);
  const replyDefaultsAvailable = Boolean(messageDraftDefaults?.byMode?.reply);
  const replyProviderLabel = messageDraftDefaults?.provider === 'OUTLOOK' ? 'Microsoft' : 'Google';

  const composerFields: DraftFields = useMemo(
    () => ({
      to: composerDraft.to,
      cc: composerDraft.cc,
      bcc: composerDraft.bcc,
      subject: composerDraft.subject,
      authoredBody: composerDraft.authoredBody,
    }),
    [composerDraft.to, composerDraft.cc, composerDraft.bcc, composerDraft.subject, composerDraft.authoredBody]
  );
  const createReplyDraft = useCallback(
    async (fields: DraftFields): Promise<{ draftContentId: number; fields: DraftFields }> => {
      const result = await createReplyDraftMutation.mutateAsync({
        sourceContentId: content.id,
        input: {
          ...(composeAccountId !== null ? { memberExternalAccountId: composeAccountId } : {}),
          mode: composerMode,
          to: splitRecipients(fields.to),
          cc: splitRecipients(fields.cc),
          bcc: splitRecipients(fields.bcc),
          ...(fields.subject.trim().length > 0 ? { subject: fields.subject.trim() } : {}),
          body: fields.authoredBody,
        },
      });
      return { draftContentId: result.draftContentId, fields: draftFieldsFromLists(result.resolved) };
    },
    [createReplyDraftMutation, content.id, composeAccountId, composerMode]
  );
  const replaceComposerFields = useCallback((fields: DraftFields) => {
    setComposerDraft((current) => ({ ...current, ...fields }));
  }, []);
  const replyBaseline: DraftFields = useMemo(() => {
    const prefill = buildComposerPrefillFromDefaults(content, composerMode);
    return {
      to: prefill.to,
      cc: prefill.cc,
      bcc: prefill.bcc,
      subject: prefill.subject,
      authoredBody: prefill.authoredBody,
    };
  }, [content, composerMode]);
  const lifecycle = useMessageDraftLifecycle({
    flushKey: `inline-reply-${content.id}`,
    active: composerOpen,
    fields: composerFields,
    baseline: replyBaseline,
    draftContentId: boundDraftContentId,
    dirty: composerDirty,
    onBound: setBoundDraftContentId,
    onDirtyChange: setComposerDirty,
    onReplaceFields: replaceComposerFields,
    create: createReplyDraft,
    readRemoteFields: (remote) => {
      if (remote.contentType !== 'MESSAGE') return null;
      const item = remote.conversation?.items.find((entry) => entry.id === remote.id);
      return item?.resumableDraft ? draftFieldsFromLists(item.resumableDraft) : null;
    },
  });
  const isDraftEmpty = (state: ComposerDraftState): boolean =>
    state.to.length === 0 &&
    state.cc.length === 0 &&
    state.bcc.length === 0 &&
    state.subject.length === 0 &&
    state.authoredBody.length === 0 &&
    state.quotedBody.length === 0;

  const latestConversationItemId = useMemo(() => {
    if (conversationItems.length === 0) {
      return null;
    }
    return conversationItems[conversationItems.length - 1]?.id ?? null;
  }, [conversationItems]);

  const draftConversationItemIds = useMemo(
    () => conversationItems.filter((item) => item.isDraft).map((item) => item.id),
    [conversationItems]
  );
  const draftIdsKey = draftConversationItemIds.join(',');

  const resumableDraftItem = useMemo(() => {
    for (let index = conversationItems.length - 1; index >= 0; index -= 1) {
      const item = conversationItems[index];
      if (item?.resumableDraft) {
        return item;
      }
    }
    return null;
  }, [conversationItems]);
  const resumableDraftId = resumableDraftItem?.resumableDraft?.draftContentId ?? null;

  const displayConversationItems = useMemo(
    () => conversationItems.filter((item) => item.id !== boundDraftContentId),
    [conversationItems, boundDraftContentId]
  );

  const [expandedMessageIds, setExpandedMessageIds] = useState<Set<number>>(() => {
    const items = content.conversation?.items ?? [];
    if (items.length === 0) {
      return new Set();
    }
    const last = items[items.length - 1];
    const initial = new Set<number>(items.filter((item) => item.isDraft).map((item) => item.id));
    if (last) {
      initial.add(last.id);
    }
    return initial;
  });
  const [expandedEnvelopeIds, setExpandedEnvelopeIds] = useState<Set<number>>(new Set());
  const [messageTrimmedVisibleIds, setMessageTrimmedVisibleIds] = useState<Set<number>>(new Set());
  const [threadMiddleExpanded, setThreadMiddleExpanded] = useState(false);
  const [pendingHighlight, setPendingHighlight] = useState<MessageHighlightQuoteDetail | null>(null);
  const location = useLocation();
  const consumedNavStateKey = useRef<string | null>(null);
  // Reveal whatever collapse state hides the source message (consolidated middle, collapsed
  // card, trimmed/quoted body) so the quote text is in the DOM, then highlight once the
  // re-render has committed — see the pendingHighlight effect.
  const revealAndHighlight = useCallback((highlight: MessageHighlightQuoteDetail) => {
    setThreadMiddleExpanded(true);
    setExpandedMessageIds((current) =>
      current.has(highlight.sourceContentId) ? current : new Set(current).add(highlight.sourceContentId)
    );
    setMessageTrimmedVisibleIds((current) =>
      current.has(highlight.sourceContentId) ? current : new Set(current).add(highlight.sourceContentId)
    );
    setPendingHighlight(highlight);
  }, []);

  useEffect(() => {
    setThreadMiddleExpanded(false);
    setMessageTrimmedVisibleIds(new Set());
    if (latestConversationItemId === null) {
      setExpandedMessageIds(new Set());
      return;
    }
    setExpandedMessageIds(
      new Set([latestConversationItemId, ...draftIdsKey.split(',').filter(Boolean).map(Number)])
    );
  }, [content.id, conversationItems.length, latestConversationItemId, draftIdsKey]);

  useEffect(() => {
    const resumable = resumableDraftItem?.resumableDraft ?? null;
    if (resumable) {
      setBoundDraftContentId(resumable.draftContentId);
      setComposerMode(resumable.sourceMode);
      if (!composerDirty) {
        setComposerDraft(buildComposerPrefillFromResumable(content, resumable));
      }
      setComposerOpen(true);
      return;
    }
    setBoundDraftContentId(null);
    setComposerOpen(false);
    setComposerMode('reply');
    setComposerDraft({ ...EMPTY_COMPOSER_DRAFT });
    setComposerAttachments([]);
    setComposerDirty(false);
    // Bind and unbind only: content changes to a bound draft flow through the lifecycle's
    // re-hydrate path, so one Neo revision is never written twice.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content.id, resumableDraftId]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    const onToggleDraft = () => {
      setComposerMode('reply');
      setComposerDraft((current) => {
        if (!isDraftEmpty(current)) {
          return current;
        }
        return buildComposerPrefillFromDefaults(content, 'reply');
      });
      setComposerOpen((current) => !current);
    };
    const onTogglePromote = () => setShowPromoteEditor((current) => !current);
    const onRunExtraction = async () => {
      if (isExtractingRef.current) {
        return;
      }
      isExtractingRef.current = true;
      const toastId = toast.loading('Extracting obligations…');
      try {
        const result = await extractMessageObligations(content.id);
        invalidateObligationViews(queryClient);
        toast.success(
          `${result.createdExtractIds.length} extract(s) created${
            result.skipped.length > 0 ? `, ${result.skipped.length} skipped` : ''
          }.`,
          {
            id: toastId,
            duration: 8000,
            description:
              result.reasoning.length > 0 ? (
                <div className="space-y-0.5">
                  {result.reasoning.map((item) => (
                    <div key={item.extractId}>
                      {item.kind}: {item.actionee ?? 'Unassigned'} ({item.actioneeRule})
                    </div>
                  ))}
                </div>
              ) : undefined,
          }
        );
      } catch (error) {
        toast.error(getAxiosErrorDisplayMessage(error, 'Extraction failed.'), { id: toastId });
      } finally {
        isExtractingRef.current = false;
      }
    };
    const onHighlightQuote = (event: Event) => {
      const detail = (event as CustomEvent<unknown>).detail;
      if (isMessageHighlightQuoteDetail(detail)) {
        revealAndHighlight(detail);
      } else {
        setPendingHighlight(null);
        if (typeof CSS !== 'undefined' && 'highlights' in CSS) {
          CSS.highlights.delete(EXTRACT_QUOTE_HIGHLIGHT_NAME);
        }
      }
    };
    window.addEventListener(MESSAGE_TOGGLE_DRAFT_EVENT, onToggleDraft);
    window.addEventListener(MESSAGE_TOGGLE_PROMOTE_EVENT, onTogglePromote);
    window.addEventListener(MESSAGE_RUN_EXTRACTION_EVENT, onRunExtraction);
    window.addEventListener(MESSAGE_HIGHLIGHT_QUOTE_EVENT, onHighlightQuote);
    return () => {
      window.removeEventListener(MESSAGE_TOGGLE_DRAFT_EVENT, onToggleDraft);
      window.removeEventListener(MESSAGE_TOGGLE_PROMOTE_EVENT, onTogglePromote);
      window.removeEventListener(MESSAGE_RUN_EXTRACTION_EVENT, onRunExtraction);
      window.removeEventListener(MESSAGE_HIGHLIGHT_QUOTE_EVENT, onHighlightQuote);
      if (typeof CSS !== 'undefined' && 'highlights' in CSS) {
        CSS.highlights.delete(EXTRACT_QUOTE_HIGHLIGHT_NAME);
      }
    };
  }, [content, queryClient, revealAndHighlight]);

  useEffect(() => {
    if (consumedNavStateKey.current === location.key) {
      return;
    }
    consumedNavStateKey.current = location.key;
    const highlight = (location.state as { highlight?: unknown } | null)?.highlight;
    if (isMessageHighlightQuoteDetail(highlight)) {
      revealAndHighlight(highlight);
    }
  }, [location.key, location.state, revealAndHighlight]);

  // Runs after the source message has been expanded (collapse state above), so the quote text is
  // now in the DOM for highlightQuoteInMessageBody to find, highlight, and scroll into view.
  useEffect(() => {
    if (!pendingHighlight) {
      return;
    }
    highlightQuoteInMessageBody(pendingHighlight.quote);
    setPendingHighlight(null);
  }, [pendingHighlight, expandedMessageIds, messageTrimmedVisibleIds, threadMiddleExpanded]);

  useEffect(() => {
    if (content.tracking?.isRead === true) {
      return;
    }
    if (markedReadForContentIdRef.current === content.id) {
      return;
    }
    markedReadForContentIdRef.current = content.id;
    markAsReadMutation.mutate(content.id);
  }, [content.id, content.tracking?.isRead, markAsReadMutation]);

  if (!sourceConversationItem && !parsed) {
    return (
      <Notice tone="destructive" data-testid="details-message-malformed" className="p-4 text-base">
        <h3 className="font-medium text-destructive">Malformed message payload</h3>
        <p className="mt-2 text-sm text-destructive">
          This communication record cannot be rendered safely because required message fields are invalid.
        </p>
        <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-destructive">
          {errors.map((error) => (
            <li key={error}>{error}</li>
          ))}
        </ul>
      </Notice>
    );
  }

  const toggleConversationMessage = (messageId: number): void => {
    setExpandedMessageIds((current) => {
      const next = new Set(current);
      if (next.has(messageId)) {
        next.delete(messageId);
        setExpandedEnvelopeIds((env) => {
          const envNext = new Set(env);
          envNext.delete(messageId);
          return envNext;
        });
        setMessageTrimmedVisibleIds((trim) => {
          const trimNext = new Set(trim);
          trimNext.delete(messageId);
          return trimNext;
        });
      } else {
        next.add(messageId);
      }
      return next;
    });
  };

  const toggleMessageTrimmedVisible = (messageId: number): void => {
    setMessageTrimmedVisibleIds((current) => {
      const next = new Set(current);
      if (next.has(messageId)) {
        next.delete(messageId);
      } else {
        next.add(messageId);
      }
      return next;
    });
  };
  const openComposerInMode = (mode: MessageDraftMode): void => {
    if (composerOpen && boundDraftContentId !== null) {
      return;
    }
    setBoundDraftContentId(null);
    setComposerMode(mode);
    setComposerDraft((current) => {
      if (!isDraftEmpty(current)) {
        return current;
      }
      return buildComposerPrefillFromDefaults(content, mode);
    });
    setComposerOpen(true);
  };

  const handleComposerModeChange = (next: MessageDraftMode): void => {
    if (next === composerMode) {
      return;
    }
    const previousPrefill = buildComposerPrefillFromDefaults(content, composerMode);
    const userEdited =
      composerDraft.to !== previousPrefill.to ||
      composerDraft.cc !== previousPrefill.cc ||
      composerDraft.bcc !== previousPrefill.bcc ||
      composerDraft.subject !== previousPrefill.subject ||
      composerDraft.authoredBody !== previousPrefill.authoredBody ||
      composerDraft.quotedBody !== previousPrefill.quotedBody;
    setComposerMode(next);
    if (!userEdited) {
      setComposerDraft(buildComposerPrefillFromDefaults(content, next));
    }
  };

  const handleQuickReplyChip = (text: string): void => {
    if (!(composerOpen && boundDraftContentId !== null)) {
      setBoundDraftContentId(null);
      setComposerMode('reply');
    }
    setComposerDraft((prev) => {
      const seeded = isDraftEmpty(prev) ? buildComposerPrefillFromDefaults(content, 'reply') : prev;
      const nextAuthored = seeded.authoredBody.length === 0 ? text : `${text}\n\n${seeded.authoredBody}`;
      return { ...seeded, authoredBody: nextAuthored };
    });
    setComposerDirty(true);
    lifecycle.noteEdit();
    setComposerOpen(true);
  };

  const handleComposerCancel = async (): Promise<void> => {
    try {
      const outcome = await lifecycle.close();
      if (outcome === 'kept') {
        toast.success('Draft kept');
      }
    } catch (error) {
      toast.error(getAxiosErrorDisplayMessage(error, 'Failed to close the reply.'));
      return;
    }
    setComposerOpen(false);
    setBoundDraftContentId(null);
    setComposerDraft({ ...EMPTY_COMPOSER_DRAFT });
    setComposerAttachments([]);
    setComposerDirty(false);
  };

  const handleSendInline = async (): Promise<void> => {
    if (composerMode === 'forward' && splitRecipients(composerDraft.to).length === 0) {
      toast.error('Forward requires at least one recipient in To.');
      return;
    }
    setIsSendingInline(true);
    try {
      const draftContentId = await lifecycle.flush();
      if (draftContentId === null) {
        toast.error('Nothing to send.');
        return;
      }
      for (const file of composerAttachments) {
        const created = await createContent({
          contentType: file.type.startsWith('image/') ? 'PHOTO' : 'FILE',
          memberId: 0,
          title: file.name,
          file,
        });
        await addContentAttachment(draftContentId, { childContentId: created.id });
      }
      await sendDraftMutation.mutateAsync({ draftContentId, refreshContentId: content.id });
      toast.success('Reply sent.');
      setComposerOpen(false);
      setBoundDraftContentId(null);
      setComposerDraft({ ...EMPTY_COMPOSER_DRAFT });
      setComposerAttachments([]);
      setComposerDirty(false);
    } catch (error) {
      toast.error(getAxiosErrorDisplayMessage(error, 'Failed to send reply.'));
    } finally {
      setIsSendingInline(false);
    }
  };

  const handlePromoteToDocument = async (): Promise<void> => {
    setIsPromoting(true);
    try {
      const result = await executeTool<
        { sourceContentIds: number[] },
        { rootDocumentId: number }
      >('content.composeDocumentFromContent', { sourceContentIds: [content.id] });

      if (result.status === 'PENDING_APPROVAL') {
        toast.success('Promotion queued in Mission Control for approval.');
        return;
      }
      if (result.status === 'FAILED') {
        const message = result.error ?? 'Failed to promote message to document.';
        if (message.includes('CONTENT_ALREADY_LINKED_TO_DOCUMENT')) {
          toast.error('This message is already linked to a document.');
          return;
        }
        toast.error(message);
        return;
      }
      if (!result.output) {
        toast.error('Promotion returned no document.');
        return;
      }
      toast.success('Message promoted to document.');
      navigate(toContentHref(String(result.output.rootDocumentId)));
    } catch (error) {
      toast.error(getAxiosErrorDisplayMessage(error, 'Failed to promote message to document.'));
    } finally {
      setIsPromoting(false);
    }
  };

  const shouldConsolidateThread = displayConversationItems.length > 4;
  const hiddenMiddleItems = shouldConsolidateThread ? displayConversationItems.slice(1, -2) : [];
  const hiddenMiddleCount = hiddenMiddleItems.length;

  const renderConversationMessageItem = (item: MessageConversationItem) => {
    const isExpanded = expandedMessageIds.has(item.id);
    const isEnvelopeExpanded = expandedEnvelopeIds.has(item.id);
    const readableBody = getReadableMessageBody(item);
    const expandedHtml = getExpandedSanitizedHtml(item);
    const bodyParts = getExpandedBodyParts(expandedHtml, readableBody);
    const splitHtmlBody =
      bodyParts?.mode === 'html' ? splitHtmlAtFirstRichLayoutBoundary(bodyParts.main) : null;
    const richHtmlBlock = splitHtmlBody?.richHtml ?? null;
    const shouldCenterHtmlBody = richHtmlBlock ? shouldCenterRichHtmlBlock(richHtmlBlock) : false;
    const trimmedVisible = messageTrimmedVisibleIds.has(item.id);
    const recipientSummary = getRecipientSummary(item);
    const senderMailDomain = getSenderMailDomain(item.from?.email ?? null);
    return (
      <article key={item.id} className="border-b border-border/50 py-2">
        {isExpanded ? (
          <div className="space-y-3 px-2 pb-3 pt-2">
            <div className="flex w-full items-start gap-3">
              <button
                type="button"
                onClick={() => toggleConversationMessage(item.id)}
                className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-muted text-sm font-medium text-foreground hover:opacity-90"
                aria-label="Collapse message"
              >
                {getAvatarText(item)}
              </button>
              <div className="min-w-0 flex-1 space-y-0.5">
                <button
                  type="button"
                  onClick={() => toggleConversationMessage(item.id)}
                  className="m-0 flex w-full min-w-0 items-baseline justify-between gap-3 rounded p-0 text-left hover:bg-muted/30"
                >
                  <span className="flex min-w-0 items-center gap-2">
                    <p className="truncate text-sm font-medium">{getDisplayName(item)}</p>
                    {item.isDraft ? (
                      <span className="shrink-0 rounded-full border border-warning-border bg-warning-subtle px-2 py-0.5 text-xs font-medium text-warning">
                        Draft
                      </span>
                    ) : null}
                  </span>
                  <span className="shrink-0 text-xs text-muted-foreground">
                    {formatReadableTimestamp(getConversationTimelineAt(item))}
                  </span>
                </button>
                <Popover
                  open={isEnvelopeExpanded}
                  onOpenChange={(open) => {
                    setExpandedEnvelopeIds((current) => {
                      const next = new Set(current);
                      if (open) {
                        next.add(item.id);
                      } else {
                        next.delete(item.id);
                      }
                      return next;
                    });
                  }}
                >
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      onClick={(event) => event.stopPropagation()}
                      aria-expanded={isEnvelopeExpanded}
                      aria-label="Show message details"
                      className="m-0 flex w-full min-w-0 items-center justify-start gap-0 p-0 text-left text-sm font-normal leading-normal text-muted-foreground hover:text-foreground"
                    >
                      <span className="min-w-0 shrink truncate">
                        {recipientSummary ?? 'sending info'}
                      </span>
                      <Info
                        className="pointer-events-none ml-1.5 h-3.5 w-3.5 shrink-0 text-muted-foreground/80"
                        aria-hidden
                        strokeWidth={2}
                      />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent
                    align="start"
                    side="bottom"
                    sideOffset={6}
                    className="w-[min(calc(100vw-2rem),26rem)] max-w-none rounded-sm border bg-background p-4 text-sm text-foreground shadow-lg"
                    onClick={(event) => event.stopPropagation()}
                  >
                    <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-5 gap-y-2.5">
                      <dt className="pt-0.5 text-right text-muted-foreground">from</dt>
                      <dd className="min-w-0 break-words">
                        {item.from?.email ? (
                          <>
                            {item.from?.name?.trim() ? (
                              <span className="font-medium">{item.from.name.trim()}</span>
                            ) : null}
                            {item.from?.name?.trim() ? ' ' : null}
                            <span>&lt;{item.from.email}&gt;</span>
                          </>
                        ) : (
                          'N/A'
                        )}
                      </dd>
                      <dt className="pt-0.5 text-right text-muted-foreground">to</dt>
                      <dd className="min-w-0 break-words">
                        {item.to.length > 0 ? joinConversationAddresses(item.to) : 'N/A'}
                      </dd>
                      {item.cc.length > 0 ? (
                        <>
                          <dt className="pt-0.5 text-right text-muted-foreground">cc</dt>
                          <dd className="min-w-0 break-words">{joinConversationAddresses(item.cc)}</dd>
                        </>
                      ) : null}
                      {item.bcc.length > 0 ? (
                        <>
                          <dt className="pt-0.5 text-right text-muted-foreground">bcc</dt>
                          <dd className="min-w-0 break-words">{joinConversationAddresses(item.bcc)}</dd>
                        </>
                      ) : null}
                      <dt className="pt-0.5 text-right text-muted-foreground">date</dt>
                      <dd className="min-w-0 break-words">
                        {formatReadableTimestamp(getConversationTimelineAt(item))}
                      </dd>
                      <dt className="pt-0.5 text-right text-muted-foreground">subject</dt>
                      <dd className="min-w-0 break-words">
                        {item.subject?.trim() ? item.subject.trim() : '(no subject)'}
                      </dd>
                      {senderMailDomain ? (
                        <>
                          <dt className="pt-0.5 text-right text-muted-foreground">mailed-by</dt>
                          <dd className="min-w-0 break-words">{senderMailDomain}</dd>
                        </>
                      ) : null}
                    </dl>
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            {bodyParts ? (
              <div
                className={
                  bodyParts.mode === 'html'
                    ? 'min-w-0 overflow-x-auto py-3 sm:py-4'
                    : 'min-w-0'
                }
              >
                {bodyParts.mode === 'html' ? (
                  <>
                    {shouldCenterHtmlBody && splitHtmlBody?.leadHtml.trim() ? (
                      <div
                        className={
                          bodyParts.quoted
                            ? `${messageBodyHtmlEmailClassName} pb-2 [&>:last-child]:mb-0`
                            : messageBodyHtmlEmailClassName
                        }
                        dangerouslySetInnerHTML={{ __html: splitHtmlBody.leadHtml }}
                      />
                    ) : null}
                    <div
                      className={
                        bodyParts.quoted
                          ? `${shouldCenterHtmlBody ? messageBodyHtmlEmailCenteredClassName : messageBodyHtmlEmailClassName} pb-0 [&>:last-child]:mb-0`
                          : shouldCenterHtmlBody
                            ? messageBodyHtmlEmailCenteredClassName
                            : messageBodyHtmlEmailClassName
                      }
                      dangerouslySetInnerHTML={{
                        __html: shouldCenterHtmlBody && richHtmlBlock ? richHtmlBlock : bodyParts.main,
                      }}
                    />
                  </>
                ) : (
                  <Markdown
                    className={
                      bodyParts.quoted
                        ? `${messageBodyProseClassName} pb-0 [&>:last-child]:mb-0`
                        : messageBodyProseClassName
                    }
                    components={messageBodyMarkdownComponents}
                  >
                    {bodyParts.main}
                  </Markdown>
                )}
                {bodyParts.quoted ? (
                  <TooltipProvider delayDuration={400}>
                    <div className="px-[52px] pt-0">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            type="button"
                            onClick={() => toggleMessageTrimmedVisible(item.id)}
                            aria-expanded={trimmedVisible}
                            aria-label={trimmedVisible ? 'Hide quoted thread content' : 'Show trimmed content'}
                            className="inline-flex h-6 min-w-7 items-center justify-center rounded-md border border-border/70 bg-muted/60 px-2 text-muted-foreground shadow-sm hover:bg-muted hover:text-foreground"
                          >
                            <Ellipsis className="h-3 w-3" strokeWidth={2} />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent side="bottom">
                          {trimmedVisible ? 'Hide expanded content' : 'Show trimmed content'}
                        </TooltipContent>
                      </Tooltip>
                    </div>
                    {trimmedVisible ? (
                      <div className={messageBodyQuotedShellClassName}>
                        {bodyParts.mode === 'html' ? (
                          <div
                            className={messageBodyHtmlEmailQuotedInnerClassName}
                            dangerouslySetInnerHTML={{ __html: bodyParts.quoted }}
                          />
                        ) : (
                          <Markdown
                            className={messageBodyQuotedInnerClassName}
                            components={messageBodyMarkdownComponents}
                          >
                            {bodyParts.quoted}
                          </Markdown>
                        )}
                      </div>
                    ) : null}
                  </TooltipProvider>
                ) : null}
              </div>
            ) : (
              <p className="px-[52px] text-sm text-muted-foreground">No readable body available.</p>
            )}
            {item.attachments.length > 0 || item.hasAttachments ? (
              <div className="space-y-1.5 pl-[52px] pr-2 pt-2">
                <p className="text-xs font-medium text-muted-foreground">Attachments</p>
                <ContentAttachments
                  parentContentId={item.id}
                  title=""
                  emptyText="No attachments"
                  breadcrumbLabel={CANONICAL_LABELS.message}
                  layout="embedded"
                />
              </div>
            ) : null}
          </div>
        ) : (
          <button
            type="button"
            onClick={() => toggleConversationMessage(item.id)}
            className="flex w-full items-start gap-3 rounded px-2 py-2 text-left hover:bg-muted/20"
          >
            <div className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-muted text-sm font-medium text-foreground">
              {getAvatarText(item)}
            </div>
            <div className="min-w-0 flex-1 space-y-0.5">
              <div className="flex min-w-0 items-baseline justify-between gap-3">
                <span className="flex min-w-0 items-center gap-2">
                  <p className="truncate text-sm font-medium">{getDisplayName(item)}</p>
                  {item.isDraft ? (
                    <span className="shrink-0 rounded-full border border-warning-border bg-warning-subtle px-2 py-0.5 text-xs font-medium text-warning">
                      Draft
                    </span>
                  ) : null}
                </span>
                <span className="shrink-0 text-xs text-muted-foreground">
                  {formatReadableTimestamp(getConversationTimelineAt(item))}
                </span>
              </div>
              <p className="line-clamp-1 text-sm text-muted-foreground">{getCollapsedPreview(item)}</p>
            </div>
          </button>
        )}
      </article>
    );
  };

  const renderThreadConsolidationControl = (expanded: boolean) => (
    <div className="relative border-b border-border/50">
      <div
        className="pointer-events-none absolute inset-y-0 left-0 right-0 flex items-center px-3"
        aria-hidden
      >
        <div className="flex w-full flex-col justify-center gap-0.5">
          <div className="h-px w-full bg-border" />
          <div className="h-px w-full bg-border" />
        </div>
      </div>
      <button
        type="button"
        onClick={() => setThreadMiddleExpanded(!expanded)}
        className="relative flex w-full items-center gap-3 rounded px-2 py-2.5 text-left hover:bg-muted/20"
        aria-expanded={expanded}
      >
        <span className="group/consolidated-avatar relative z-10 flex h-9 w-9 shrink-0 items-center justify-center rounded-full border-2 border-background bg-background text-sm font-medium text-muted-foreground shadow-sm ring-1 ring-border">
          <span className="tabular-nums group-hover/consolidated-avatar:hidden">{hiddenMiddleCount}</span>
          {expanded ? (
            <ChevronsDownUp
              className="hidden h-4 w-4 shrink-0 text-muted-foreground group-hover/consolidated-avatar:block"
              aria-hidden
            />
          ) : (
            <ChevronsUpDown
              className="hidden h-4 w-4 shrink-0 text-muted-foreground group-hover/consolidated-avatar:block"
              aria-hidden
            />
          )}
        </span>
        <span className="relative z-10 text-sm text-muted-foreground">
          {expanded
            ? `Hide ${hiddenMiddleCount} message${hiddenMiddleCount === 1 ? '' : 's'}`
            : `${hiddenMiddleCount} message${hiddenMiddleCount === 1 ? '' : 's'} hidden`}
        </span>
      </button>
    </div>
  );

  const sendableProvider =
    messageDraftDefaults?.provider === 'GMAIL' || messageDraftDefaults?.provider === 'OUTLOOK';
  const showConnectAccountHint = sendableProvider && !replyCapable;
  const hasBoundDraftComposer = composerOpen && boundDraftContentId !== null;
  const showThreadFoot = replyCapable || showConnectAccountHint || hasBoundDraftComposer;

  const renderThreadFoot = () => {
    if (!showThreadFoot) {
      return null;
    }
    if (composerOpen) {
      return (
        <div className="flex w-full items-start gap-3 px-2 pb-2 pt-3">
          <div
            className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-muted text-sm font-medium text-foreground"
            aria-hidden
          >
            {composerMemberAvatarText}
          </div>
          <div className="min-w-0 flex-1" onBlur={lifecycle.saveNow}>
            <MessageInlineReplyComposer
              sourceMessage={content}
              mode={composerMode}
              draft={composerDraft}
              attachments={composerAttachments}
              onModeChange={boundDraftContentId !== null ? () => undefined : handleComposerModeChange}
              onDraftChange={(next) => {
                setComposerDraft(next);
                setComposerDirty(true);
                lifecycle.noteEdit();
              }}
              onAttachmentsChange={setComposerAttachments}
              onSend={() => void handleSendInline()}
              onCancel={() => void handleComposerCancel()}
              revision={lifecycle.revision}
              isSending={isSendingInline || sendDraftMutation.isPending}
            />
          </div>
        </div>
      );
    }
    return (
      <div className="space-y-2 px-2 pb-2 pt-3 pl-[52px]">
        {showConnectAccountHint ? (
          <div className="rounded-md border border-dashed bg-muted/20 p-3 text-sm text-muted-foreground">
            Connect an active {replyProviderLabel} account with send access to reply.
          </div>
        ) : (
          <>
            {replyDefaultsAvailable ? (
              <div role="group" aria-label="Quick replies" className="flex flex-wrap gap-2">
                {QUICK_REPLY_CHIPS.map((chip) => (
                  <button
                    key={chip}
                    type="button"
                    onClick={() => handleQuickReplyChip(chip)}
                    className="inline-flex h-7 items-center rounded-full border border-border bg-background px-3 text-xs text-muted-foreground hover:bg-muted/40 hover:text-foreground"
                  >
                    {chip}
                  </button>
                ))}
              </div>
            ) : null}
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => openComposerInMode('reply')}
              >
                <Reply className="mr-2 h-4 w-4" />
                Reply
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => openComposerInMode('forward')}
              >
                <Forward className="mr-2 h-4 w-4" />
                Forward
              </Button>
              <TooltipProvider delayDuration={400}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      aria-label="Add reaction"
                      onClick={() => {}}
                    >
                      <SmilePlus className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Reactions coming soon</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </>
        )}
      </div>
    );
  };

  return (
    <div data-testid="details-message" className="space-y-3 pb-28">
      {showPromoteEditor ? (
        <div className="rounded-md border bg-card p-4">
          <div className="space-y-3 rounded-md border bg-muted/20 p-3">
            <h4 className="text-sm font-medium">Promote Message to Document</h4>
            <p className="text-sm text-muted-foreground">
              A new Document will be created from this message. The title and description are generated automatically from the message content.
            </p>
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowPromoteEditor(false)}
                disabled={isPromoting}
              >
                Cancel
              </Button>
              <Button type="button" onClick={handlePromoteToDocument} disabled={isPromoting}>
                {isPromoting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Promote to Document
              </Button>
            </div>
          </div>
        </div>
      ) : null}

      {displayConversationItems.length === 0 ? (
        <p className="px-1 text-sm text-muted-foreground">
          No derived conversation projection available for this message.
        </p>
      ) : shouldConsolidateThread ? (
        <div className="space-y-0">
          {renderConversationMessageItem(displayConversationItems[0]!)}
          {threadMiddleExpanded ? (
            <>
              {renderThreadConsolidationControl(true)}
              {hiddenMiddleItems.map((item) => renderConversationMessageItem(item))}
            </>
          ) : (
            renderThreadConsolidationControl(false)
          )}
          {renderConversationMessageItem(displayConversationItems[displayConversationItems.length - 2]!)}
          {renderConversationMessageItem(displayConversationItems[displayConversationItems.length - 1]!)}
        </div>
      ) : (
        <div className="space-y-0">
          {displayConversationItems.map((item) => renderConversationMessageItem(item))}
        </div>
      )}

      {renderThreadFoot()}
    </div>
  );
}
